/**
 * axistime.ts — HOW A TIMESTAMP IS SPELLED ON A PANE. Formatting only; never a shift.
 *
 * Andrei, 2026-08-25: *"время в Web UI на всех 3-х pane-ах должно быть в America/NewYork
 * таймзоне"*.
 *
 * WHAT WAS ACTUALLY WRONG — there was NO localization at all, which is a different defect from
 * "the wrong zone". Measured before this module existed:
 *
 *     grep -E 'timeZone|toLocale|localization' chartapp/web/src/chartpane.ts main.ts
 *     -> not one match
 *
 * Bars reach lightweight-charts as `UTCTimestamp` (`chartpane.ts::tintInside`) and the library
 * spells a `UTCTimestamp` axis in UTC by default, with no marking of the zone anywhere on the
 * pane. So the axis read UTC while every decision this app supports is taken on the New York
 * session, a divergence of 4 or 5 hours depending on the date, and SILENT in both directions:
 * nothing on screen said which zone the label was in.
 *
 * WHY IT IS NOT COSMETIC: ICT is stated in New York session terms (the 09:30 open, the killzones,
 * the weekly profile's Monday-Tuesday consolidation). A swing that printed at 09:30 NY appears at
 * 13:30 on a UTC axis in summer and 14:30 in winter — and that DST split is exactly why a
 * hardcoded −4/−5 offset is the wrong fix and `Intl` is the right one: the platform already owns
 * the transition table, and this app must not grow a second one.
 *
 * THE ONE INVARIANT: NO TIMESTAMP IS EVER MOVED. Every function here takes a UTC epoch second and
 * returns a STRING. Nothing in this module is on the path that stores, compares or fetches a time,
 * so `bars_cache`, `/api/bars`, `first_bar_after` and the outcome journal keep the identical UTC
 * values they always had — shifting the data would break reconciliation against the venue, which
 * reports in UTC. Formatting is a presentation fact; the timestamp is a money fact.
 *
 * TWO ZONES, DELIBERATELY, AND THE SPLIT IS `needsClockOnAxis`:
 *
 *   * BELOW 1D (4h/1h/15m/1m) — America/New_York. The label carries a clock, and the clock is the
 *     thing Andrei asked for.
 *   * 1D / 1W / 1M — UTC, i.e. the bar's own date as the EXCHANGE stamps it, and this is the
 *     considered decision the card required rather than an omission. Those labels carry no time,
 *     only a date, and a daily bar opens at 00:00 UTC: re-spelled in New York that instant is
 *     19:00 or 20:00 of the PREVIOUS calendar day, so every daily label would move one day back.
 *     A weekly bar would then be labelled with the Sunday before its own Monday open and stop
 *     agreeing with the week range, `ict/weekly_profile.py`'s Monday-anchored logic, and the
 *     `/api/bars` payload a reader compares it against. The bar IS a UTC calendar day; naming it
 *     by a different day's date would be a wrong statement about the data, not a localization.
 *
 * BOTH ZONES ARE MARKED IN THE UI (`zoneTag`), because an unlabelled axis is the same silent
 * divergence pointing the other way.
 *
 * LOCALE IS PINNED TO en-US and the `locale` argument lightweight-charts passes is ignored on
 * purpose: this app's UI is English everywhere (repo convention), and letting the browser's
 * language choose would make a label's SHAPE depend on the machine, so a screenshot from one box
 * would not match a test on another. `hourCycle: 'h23'` rather than `hour12: false`, which some
 * engines spell as `24:00` at midnight.
 */
import { TickMarkType, type Time } from 'lightweight-charts';

/** Does this timeframe need a CLOCK on the axis, not just a date?
 *
 * Andrei, 2026-08-03: "give the user an ability to see not only dates but also time if the
 * timeframe is lower than 1d similar to how TradingView does it." Before that, every pane was
 * built with `timeVisible: false`, so a 15m axis printed only the day-of-month and read
 * `1, 2, 1, 2` -- two days of bars labelled with two digits.
 *
 * DERIVED BY EXCLUSION, not a second list: anything at or above 1D is a date, everything below it
 * carries a clock. A hand-written set of intraday timeframes would silently omit one the moment it
 * is added, which is exactly how `1m` and `4h` would have been missed.
 *
 * IT LIVES HERE, NOT IN `chartpane.ts`, SINCE 2026-08-25 (card t_69d27c87), and `chartpane`
 * re-exports it so every existing consumer is unchanged. It moved because it now decides TWO
 * things, not one: whether the axis shows a clock, AND which ZONE the label is spelled in (see
 * `labelZone`). Keeping it in `chartpane.ts` while the formatters live here would have made the two
 * modules import each other — and a cycle between the pane and its own label formatter is the kind
 * of load order that fails only in a production bundle. One direction: `chartpane` -> `axistime`.
 */
export function needsClockOnAxis(tf: string): boolean {
  const dateOnly: readonly string[] = ['1M', '1W', '1D'];
  return !dateOnly.includes(tf);
}

/** THE zone. One declaration in the whole frontend — pinned by `axistime.test.ts`. */
export const CHART_TIMEZONE = 'America/New_York';

/** The zone a DATE-ONLY label is spelled in: the exchange's own. See the module docstring. */
export const DATE_ONLY_TIMEZONE = 'UTC';

/** Which zone this timeframe's labels are spelled in. The ONE place the split is decided. */
export function labelZone(tf: string): string {
  return needsClockOnAxis(tf) ? CHART_TIMEZONE : DATE_ONLY_TIMEZONE;
}

/**
 * `Intl.DateTimeFormat` instances, memoised.
 *
 * Not a micro-optimisation: `tickMarkFormatter` is called for every visible tick on every pan and
 * zoom frame, and constructing a `DateTimeFormat` is the expensive part of `Intl`. The cache key is
 * the zone plus the options, so there is no way for one call's options to leak into another's.
 */
const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function formatter(zone: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${zone}|${JSON.stringify(opts)}`;
  let f = FORMATTERS.get(key);
  if (f === undefined) {
    f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone });
    FORMATTERS.set(key, f);
  }
  return f;
}

/**
 * A lightweight-charts `Time` as epoch SECONDS, or null when it is not a timestamp.
 *
 * Every series in this app is written with `UTCTimestamp` (`tintInside`), so the number branch is
 * the live one. The other two shapes the library allows — a `'yyyy-mm-dd'` string and a
 * `BusinessDay` object — are handled rather than cast, because a formatter that throws takes the
 * whole axis down, and returning null lets the caller fall back to the library's own text.
 */
export function timeToEpochSeconds(time: Time): number | null {
  if (typeof time === 'number') return Number.isFinite(time) ? time : null;
  if (typeof time === 'string') {
    const ms = Date.parse(`${time}T00:00:00Z`);
    return Number.isFinite(ms) ? ms / 1000 : null;
  }
  if (time !== null && typeof time === 'object' && 'year' in time) {
    const b = time as { year: number; month: number; day: number };
    return Date.UTC(b.year, b.month - 1, b.day) / 1000;
  }
  return null;
}

/**
 * ONE AXIS TICK LABEL.
 *
 * The GRANULARITY is the library's decision (`TickMarkType`) and is not second-guessed here: it
 * knows which tick is the first of its year/month/day and picks the coarsest label that still
 * disambiguates. Only the ZONE and the spelling are ours. `TickMarkType.Time` is the default arm
 * so an unknown future member still produces a clock rather than an empty axis.
 */
export function axisTickLabel(time: Time, tick: TickMarkType, tf: string): string | null {
  const secs = timeToEpochSeconds(time);
  if (secs === null) return null;
  const d = new Date(secs * 1000);
  const zone = labelZone(tf);
  switch (tick) {
    case TickMarkType.Year:
      return formatter(zone, { year: 'numeric' }).format(d);
    case TickMarkType.Month:
      return formatter(zone, { month: 'short' }).format(d);
    case TickMarkType.DayOfMonth:
      return formatter(zone, { day: 'numeric' }).format(d);
    case TickMarkType.TimeWithSeconds:
      return formatter(zone, {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
      }).format(d);
    case TickMarkType.Time:
    default:
      return formatter(zone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  }
}

/**
 * THE CROSSHAIR LABEL — the full instant under the cursor, WITH its zone named.
 *
 * This is the one label that must never be ambiguous: it is what a reader quotes when they say
 * "the sweep at 09:30". So the zone is spelled out here on every label, unlike the axis, where the
 * pane's tag says it once (a suffix on every axis tick would be three characters of noise per
 * tick).
 *
 * A date-only timeframe gets no clock, for the same reason it gets no NY conversion: printing
 * `00:00` for a daily bar would claim a precision the bar does not have.
 */
export function crosshairTimeLabel(time: Time, tf: string): string | null {
  const secs = timeToEpochSeconds(time);
  if (secs === null) return null;
  const d = new Date(secs * 1000);
  const zone = labelZone(tf);
  const date = formatter(zone, {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).format(d);
  if (!needsClockOnAxis(tf)) return `${date} ${zoneTag(tf)}`;
  const clock = formatter(zone, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
  return `${date} ${clock} ${zoneTag(tf)}`;
}

/**
 * The SHORT zone mark shown once per pane (and repeated in the crosshair label).
 *
 * Derived from the same `labelZone` the formatters use, so the tag cannot claim a zone the labels
 * are not in — two spellings of that fact is how one of them goes wrong. `NY` rather than `EST` or
 * `EDT`: the abbreviation flips at the DST boundary and would need its own lookup, which is the
 * hand-rolled offset table this module exists to avoid.
 */
export function zoneTag(tf: string): string {
  return labelZone(tf) === CHART_TIMEZONE ? 'NY' : 'UTC';
}

/** Hover text for the pane's zone tag: says what the tag governs, and what it does NOT. */
export function zoneTagTitle(tf: string): string {
  return needsClockOnAxis(tf)
    ? `Axis and crosshair times are ${CHART_TIMEZONE} (DST handled by Intl). `
      + 'Bar timestamps themselves are unchanged UTC.'
    : 'Dates on 1D/1W/1M are the exchange\'s UTC calendar dates, so a bar keeps the date it '
      + 'opened on. Intraday panes label times in America/New_York.';
}
